import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Field, Formik } from "formik";
import { string, object } from "yup";

import Spinner from "@kaizen-ui/spinner";
import Block, { BlockSection } from "@kaizen-ui/block";
import Button, { ButtonGroup } from "@kaizen-ui/button";
import Textbox from "@kaizen-ui/textbox";
import { Banner, KaizenThemeContext } from "@kaizen-ui/complete";

import {
  fetchIDMAccessCode,
  postIDMAccessCode,
  patchIDMAccessCode,
  deleteIDMAccessCode,
} from "../../../api";
import { Layout } from "../../../layout";
import AccessibilityContext from "../../../components/auth/UserPermissionContext";
import WithAuth from "../../../components/auth/WithAuth";
import WithAdminUIAuth from "../../../components/auth/WithAdminUIAuth";
import "./styles.css";

/* ── Loading skeleton ─────────────────────────────────────────── */
const Loading = () => (
  <Layout title="Loading...">
    <Spinner className="loading-spinner" size="small" />
  </Layout>
);

/* ── Blank record for the create flow ────────────────────────── */
const initState = {
  accesscodeid: 0,
  accesscode:   "",
  description:  "",
  isactive:     true,
};

/* ── Yup validation schema ───────────────────────────────────── */
const validationSchema = object().shape({
  accesscode: string()
    .required("Access Code is required.")
    .max(100, "Access Code must be 100 characters or fewer."),
  description: string()
    .required("Description is required.")
    .max(500, "Description must be 500 characters or fewer."),
});

/* ── Extract a readable string from whatever the API rejects with ──
   API rejects with:
     - a plain string  → "Access Code 'IDM11' already exists."
     - an object       → { status_message, message, title, ... }
   Never JSON.stringify a string — that wraps it in extra quotes.
   ----------------------------------------------------------------- */
function extractErrorMessage(error) {
  if (!error)                  return "An unexpected error occurred.";
  if (typeof error === "string") return error;
  if (typeof error === "object") {
    return (
      error.status_message ||
      error.message        ||
      error.title          ||
      error.detail         ||
      (Object.keys(error).length > 0
        ? JSON.stringify(error)
        : "An unexpected error occurred.")
    );
  }
  return String(error);
}

/* ── Banner helpers ──────────────────────────────────────────── */
function showError(error, setErrorMsg) {
  setErrorMsg(extractErrorMessage(error));
  setTimeout(() => setErrorMsg(""), 5000);
}

function afterSuccess(setSuccess, navigate) {
  setTimeout(() => {
    setSuccess("");
    navigate("/idmaccesscode");
  }, 2000);
}

/* ── Detail / Create / Edit page ─────────────────────────────── */
function IDMAccessCodeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData]         = useState(null);
  const [errorMsg, setErrorMsg] = useState("");   // plain string only
  const [success, setSuccess]   = useState("");

  const { colors } = useContext(KaizenThemeContext);

  const permissions     = useContext(AccessibilityContext).filter(
    (x) => x.pagename === "user"
  )[0];
  const permissionsData = useContext(AccessibilityContext);

  /* ── Permission helpers ── */
  const disableDelete = () =>
    !(data && permissions?.delete && data.accesscodeid > 0);

  const disableEdit = () =>
    !(permissions?.create || (permissions?.update && id > 0));

  const disableEditField = (fieldname) => {
    const per = permissionsData.filter((x) => x.pagename === fieldname)[0];
    return !(
      (permissions?.create || (permissions?.update && id > 0)) &&
      (permissionsData === undefined || per?.update)
    );
  };

  /* ── Load record on mount ── */
  useEffect(() => {
    if (id === "0") {
      setData({ ...initState });
    } else {
      fetchIDMAccessCode(Number(id)).then((res) => setData(res));
    }
  }, [id]);

  /* ── Delete handler ── */
  const handleDelete = () => {
    if (!window.confirm("Do you want to delete this IDM Access Code?")) return;
    deleteIDMAccessCode(Number(id))
      .then(() => {
        setSuccess("Successfully deleted IDM Access Code – " + id);
        afterSuccess(setSuccess, navigate);
      })
      .catch((err) => showError(err, setErrorMsg));
  };

  if (!data) return <Loading />;

  if (permissions != null && permissions !== undefined && !permissions.read) {
    navigate("/unauthorized");
  }

  if (permissions?.read) {
    return (
      <Formik
        initialValues={data}
        enableReinitialize
        initialTouched={{ accesscode: true, description: true, isactive: true }}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          setErrorMsg("");

          const op = id === "0" ? postIDMAccessCode : patchIDMAccessCode;

          op(values)
            .then((saved) => {
              const label = id === "0" ? "created" : "updated";
              setSuccess(`Successfully ${label} IDM Access Code – ${saved.accesscode}`);
              afterSuccess(setSuccess, navigate);
            })
            .catch((err) => showError(err, setErrorMsg))
            .finally(() => actions.setSubmitting(false));
        }}
        validationSchema={validationSchema}
        validateOnMount
      >
        {(formikProps) => (
          <Layout
            title={id === "0" ? "Create IDM Access Code" : "IDM Access Code"}
            headerChildren={
              <ButtonGroup>
                <Button
                  icon={{ name: "PlaybackCircleRewind", variant: "solid" }}
                  onClick={() => navigate(-1)}
                >
                  Go Back
                </Button>
              </ButtonGroup>
            }
          >
            {/* Fix 3 & 4: single string state, rendered directly — no JSON.stringify */}
            {errorMsg !== "" && (
              <Banner className="bannertofront" rounded status="critical" title="Error">
                {errorMsg}
              </Banner>
            )}

            {success !== "" && (
              <Banner className="bannertofront" rounded status="success" title="Success">
                {success}
              </Banner>
            )}

            <Block inline className="form-example" loading={formikProps.isSubmitting}>

              {/* ── Access Code ── */}
              <BlockSection size="full" className="hubtagfieldblocksection">
                <h4 className="hubtagfieldh4" style={{ color: colors.fontColor }}>
                  Access Code<span className="hubtagfieldspan">*</span>
                </h4>
                <Field
                  as={Textbox}
                  className="zero-left zero-right hubtagfieldtext"
                  disabled={disableEditField("user")}
                  name="accesscode"
                  values={formikProps.values.accesscode}
                  placeholder="e.g. IDM01"
                  pattern={formikProps.errors.accesscode}
                  validationMessage={formikProps.errors.accesscode}
                  required={true}
                  valid={true}
                  showValidIcon={true}
                />
              </BlockSection>

              {/* ── Description ── */}
              <BlockSection size="full" className="hubtagfieldblocksection">
                <h4 className="hubtagfieldh4" style={{ color: colors.fontColor }}>
                  Description<span className="hubtagfieldspan">*</span>
                </h4>
                <Field
                  as={Textbox}
                  className="zero-left zero-right hubtagfieldtext"
                  disabled={disableEditField("user")}
                  name="description"
                  values={formikProps.values.description}
                  placeholder="Describe this access code"
                  pattern={formikProps.errors.description}
                  validationMessage={formikProps.errors.description}
                  required={true}
                  valid={true}
                  showValidIcon={true}
                />
              </BlockSection>

              {/* ── Read-only metadata (edit mode only) ── */}
              {id !== "0" && data.createddate && (
                <BlockSection size="full" className="hubtagfieldblocksection">
                  <h4 className="hubtagfieldh4" style={{ color: colors.fontColor }}>
                    Created Date
                  </h4>
                  <Textbox
                    className="zero-left zero-right hubtagfieldtext"
                    disabled
                    value={new Date(data.createddate).toLocaleString()}
                  />
                </BlockSection>
              )}

              {id !== "0" && data.modifieddate && (
                <BlockSection size="full" className="hubtagfieldblocksection">
                  <h4 className="hubtagfieldh4" style={{ color: colors.fontColor }}>
                    Last Modified Date
                  </h4>
                  <Textbox
                    className="zero-left zero-right hubtagfieldtext"
                    disabled
                    value={new Date(data.modifieddate).toLocaleString()}
                  />
                </BlockSection>
              )}

              {/* ── Action buttons ── */}
              <BlockSection size="full">
                <Button
                  className="formsubmitbtn"
                  type="critical"
                  onClick={() => navigate("/idmaccesscode")}
                >
                  Cancel
                </Button>
                <Button
                  className="formsubmitbtn"
                  disabled={disableEdit()}
                  onClick={() => {
                    formikProps.isSubmitting = true;
                    formikProps.handleSubmit();
                  }}
                >
                  Submit
                </Button>
              </BlockSection>

              {/* ── Delete button ── */}
              <BlockSection size="full">
                {permissions?.delete ? (
                  <Button
                    className="formsubmitbtn"
                    type="critical"
                    disabled={disableDelete()}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                ) : (
                  <span></span>
                )}
              </BlockSection>

            </Block>
          </Layout>
        )}
      </Formik>
    );
  }
}

export default WithAuth(WithAdminUIAuth(IDMAccessCodeDetail));